import { useEffect, useRef } from "react";

// v0.6.8 fix — extracted from CalculatePanel.jsx (LOC cap), also for
// unit-testability (useCalcJobPoll / useDriftHealth pattern). Used to
// key off a signature string alone, so any autoDetectedRange change
// (e.g. a 2nd run added) silently overwrote a manually-edited period
// — and did so before FilingPeriodCard's CL3E warning could even
// compare against it. Now auto-apply only fires while the operator
// hasn't diverged from the last-applied range; a manual edit opts out
// until a fresh auto-detected range happens to match it again.
//
// v0.6.13 (operator report 2026-10-12) — returning to Run calculation from
// Row review re-mounts this hook with empty refs, so it treated the
// operator's chosen period as "never touched" and overwrote it with the
// uploads' raw date span (e.g. one row dated 1 October widened a September
// period). The changed period then made the auto-invalidate cascade drop
// the calculation, locking Row review. With `keepCurrent` (a calculation
// exists for this selection) the current period is adopted as the applied
// one instead of being overwritten.
//
// A2-581 W3 — the same empty-refs fault with no calculation yet: each step has
// its own panel, so a period typed on Step 2 was replaced by the file's latest
// period on Step 3. The record of what was applied is now the workflow's
// (`memory`, one ref for every panel) and outlives the panel.
export const useAutoDetectedPeriodSync = (
  autoDetectedRange, periodStart, periodEnd, setPeriodStart, setPeriodEnd,
  keepCurrent = false, memory = null,
) => {
  const ownMemory = useRef({ applied: null, seen: null });
  const mem = memory || ownMemory;
  useEffect(() => {
    if (!autoDetectedRange) return;
    const sig = `${autoDetectedRange.start}|${autoDetectedRange.end}`;
    if (sig === mem.current.seen) return;
    mem.current.seen = sig;
    // A1-93 — while a calculation is loaded the period is ITS period:
    // never written from the uploads' dates. The v0.6.13 guard only held
    // for the FIRST detection; the panel's client details arrive a beat
    // later, the range snaps to the client's tax period, and the second
    // pass overwrote the calc's period — so merely opening Run calculation
    // marked a calc with a non-default period "out of date". The calc's
    // period is recorded as the applied one, so auto-sync can resume from
    // it once no calc is loaded (Start over remounts the panel).
    if (keepCurrent) {
      if (!mem.current.applied) mem.current.applied = { start: periodStart, end: periodEnd };
      return;
    }
    // Already matches — re-arm tracking so a LATER change can resume
    // auto-applying instead of staying opted out forever.
    if (periodStart === autoDetectedRange.start && periodEnd === autoDetectedRange.end) {
      mem.current.applied = autoDetectedRange;
      return;
    }
    const untouchedSinceLastApply = !mem.current.applied
      || (periodStart === mem.current.applied.start
          && periodEnd === mem.current.applied.end);
    if (!untouchedSinceLastApply) return;
    mem.current.applied = autoDetectedRange;
    setPeriodStart(autoDetectedRange.start);
    setPeriodEnd(autoDetectedRange.end);
  }, [autoDetectedRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // A1-93 round 4 — `keepCurrent` may be the open calc's own period
  // ({start, end}). When the selection comes BACK to the calc's files (tick
  // August, untick it again), the period the files moved it to is put back
  // to the calc's — unless the operator set the period by hand meanwhile
  // (it no longer equals what auto-detect last applied).
  const keepStart = keepCurrent && typeof keepCurrent === "object" ? keepCurrent.start : null;
  const keepEnd = keepCurrent && typeof keepCurrent === "object" ? keepCurrent.end : null;
  useEffect(() => {
    if (!keepStart || !keepEnd) return;
    if (periodStart === keepStart && periodEnd === keepEnd) return;
    const auto = mem.current.applied;
    if (!auto || periodStart !== auto.start || periodEnd !== auto.end) return;   // hand-edited
    mem.current.applied = { start: keepStart, end: keepEnd };
    setPeriodStart(keepStart);
    setPeriodEnd(keepEnd);
  }, [keepStart, keepEnd]); // eslint-disable-line react-hooks/exhaustive-deps
};
